import { track } from './analytics'
import { apiGet, apiPost, ApiRequestError } from './apiClient'
import { LIVE_API, ENDPOINTS, MAIL } from './config'
import { notifyTeam } from './notify'

export interface LeadInput {
  email: string
  firstName?: string
  organisation?: string
  role?: string
  sector?: string
  /** Country, captured on public sector enquiries. */
  country?: string
  consent: boolean
  source: string
  line?: string
  /** Names the HubSpot deal pipeline the enquiry belongs on, for example 'government'. */
  pipeline?: string
  notes?: string
  /**
   * Qualifying answers, written to HubSpot as contact properties. Used by the
   * webinar registration, where institution type, seniority and function decide
   * the lane and the follow up offer.
   */
  qualification?: Record<string, string | boolean | number>
}

export interface LeadResult {
  ok: boolean
  error?: string
  alreadySubscribed?: boolean
}

/**
 * A first pass block on obvious throwaway domains. The authoritative list lives
 * on the server, because anything enforced only in the browser can be bypassed.
 */
const DISPOSABLE_DOMAINS = [
  'mailinator.com',
  'yopmail.com',
  'guerrillamail.com',
  '10minutemail.com',
  'tempmail.com',
  'trashmail.com',
  'sharklasers.com',
]

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const UNLOCK_KEY = 'cavalla.reader.unlocked'
const CONTACTS_KEY = 'cavalla.contacts'

export function validateEmail(email: string): string | null {
  const value = email.trim().toLowerCase()
  if (!value) return 'Please enter your email address.'
  if (!EMAIL_PATTERN.test(value)) return 'Please enter a valid email address.'
  const domain = value.split('@')[1]
  if (DISPOSABLE_DOMAINS.includes(domain)) {
    return 'Please use a work email address. Disposable domains are not accepted.'
  }
  return null
}

const SOURCE_LABELS: Record<string, string> = {
  'contact-form': 'Contact form enquiry',
  'cavalla-gov': 'Cavalla Gov briefing request',
  'ai-concierge': 'Concierge enquiry',
  'footer-newsletter': 'Newsletter signup',
  'ai-readiness-assessment': 'AI readiness assessment',
}

/** A readable subject for the team notice, for example "Webinar registration". */
function sourceLabel(source: string): string {
  if (SOURCE_LABELS[source]) return SOURCE_LABELS[source]
  if (source.startsWith('webinar')) {
    const session = source.split(':')[1]
    return `${source.endsWith(':replay') ? 'Webinar replay request' : 'Webinar registration'}${session ? ` (${session})` : ''}`
  }
  if (source.startsWith('resource') || source.startsWith('ebook')) return 'Resource download'
  if (source.startsWith('article') || source.startsWith('insight')) return 'Gated article signup'
  return `Website form (${source})`
}

function humanise(key: string): string {
  const spaced = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[-_]/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

function readContacts(): string[] {
  try {
    return JSON.parse(window.localStorage.getItem(CONTACTS_KEY) ?? '[]') as string[]
  } catch {
    return []
  }
}

/**
 * POST /api/leads
 *
 * The route creates or updates the HubSpot contact on the EU1 portal, maps the
 * source to the right list and sequence, sets the signed HttpOnly unlock cookie
 * for gated articles, and fires the server side conversion event. The private
 * token never reaches the browser.
 */
export async function submitLead(input: LeadInput): Promise<LeadResult> {
  const emailError = validateEmail(input.email)
  if (emailError) return { ok: false, error: emailError }
  if (!input.consent) {
    return { ok: false, error: 'Please confirm you are happy to hear from us.' }
  }

  const email = input.email.trim().toLowerCase()

  if (LIVE_API) {
    try {
      const result = await apiPost<{ alreadySubscribed?: boolean }>(ENDPOINTS.leads, {
        ...input,
        email,
        notifyTo: MAIL.inbox,
      })
      track('lead_captured', { source: input.source, line: input.line, sector: input.sector })
      return { ok: true, alreadySubscribed: result?.alreadySubscribed ?? false }
    } catch (error) {
      const message =
        error instanceof ApiRequestError
          ? error.message
          : 'We could not save your details. Please try again.'
      return { ok: false, error: message }
    }
  }

  // Demonstration path, used until the server routes are deployed. The team is
  // still told about every submission through the relay. The assessment sends
  // its own fuller notice with the score, so it is skipped here.
  if (input.source !== 'ai-readiness-assessment') {
    await notifyTeam(`${sourceLabel(input.source)}: ${input.organisation ? `${email} (${input.organisation})` : email}`, {
      Form: sourceLabel(input.source),
      Email: email,
      Name: input.firstName,
      Organisation: input.organisation,
      Role: input.role,
      Sector: input.sector,
      Country: input.country,
      'Service line': input.line,
      Pipeline: input.pipeline,
      Details: input.notes,
      ...Object.fromEntries(Object.entries(input.qualification ?? {}).map(([k, v]) => [humanise(k), String(v)])),
      'Marketing consent': input.consent ? 'Yes' : 'No',
      Page: typeof window !== 'undefined' ? window.location.href : undefined,
      'Submitted at': new Date().toUTCString(),
    })
  }

  await new Promise((resolve) => setTimeout(resolve, 300))
  const contacts = readContacts()
  const alreadySubscribed = contacts.includes(email)
  if (!alreadySubscribed) {
    contacts.push(email)
    try {
      window.localStorage.setItem(CONTACTS_KEY, JSON.stringify(contacts))
    } catch {
      /* storage unavailable, the contact still reaches the CRM */
    }
  }

  track('lead_captured', { source: input.source, line: input.line, sector: input.sector })
  return { ok: true, alreadySubscribed }
}

/**
 * GET /api/reader/status
 *
 * Reads the signed HttpOnly unlock cookie, or an active portal session, and
 * reports whether this reader has already given us an email. The cookie itself
 * is not readable from JavaScript, which is why this is a request rather than a
 * local lookup.
 */
export async function fetchReaderUnlocked(): Promise<boolean> {
  if (!LIVE_API) return isReaderUnlockedLocally()
  try {
    const result = await apiGet<{ unlocked: boolean }>(ENDPOINTS.readerStatus)
    return Boolean(result?.unlocked)
  } catch {
    return false
  }
}

/** Local mirror of the unlock state, so a reveal survives a reload offline. */
export function isReaderUnlockedLocally(): boolean {
  try {
    return window.localStorage.getItem(UNLOCK_KEY) === 'true'
  } catch {
    return false
  }
}

export function setReaderUnlocked(): void {
  try {
    window.localStorage.setItem(UNLOCK_KEY, 'true')
  } catch {
    /* the reveal still works for this session */
  }
}

export interface AssessmentResultEmail {
  email: string
  organisation?: string
  total: number
  band: string
  byDimension: { dimension: string; score: number; max: number }[]
  nextSteps: { title: string; packageName: string }[]
}

/**
 * POST /api/assessment/result
 *
 * Emails the visitor their score, band and three next steps through Resend, from
 * MAIL.from, blind copying MAIL.bcc so the team sees every completed assessment.
 * The score band is also written to the HubSpot contact by the same route.
 *
 * Failure is reported but never blocks the on screen result: the visitor has
 * already answered twelve questions and must see their score regardless.
 */
export async function emailAssessmentResult(
  input: AssessmentResultEmail,
): Promise<{ ok: boolean; simulated?: boolean; error?: string }> {
  if (!LIVE_API) {
    // No server route yet, so the visitor's own copy cannot be sent and the
    // caller must not promise one. The team is still told through the relay.
    const who = input.organisation ? `${input.email} (${input.organisation})` : input.email
    await notifyTeam(`AI readiness assessment completed: ${who}`, {
      Email: input.email,
      Organisation: input.organisation,
      Score: `${input.total} out of 100`,
      Band: input.band,
      ...Object.fromEntries(input.byDimension.map((d) => [d.dimension, `${d.score} of ${d.max}`])),
      'Next steps': input.nextSteps.map((s) => s.title).join('; '),
      'Completed at': new Date().toUTCString(),
    })
    return { ok: false, simulated: true }
  }

  try {
    await apiPost(ENDPOINTS.assessmentResult, input)
    return { ok: true }
  } catch (error) {
    return {
      ok: false,
      error:
        error instanceof ApiRequestError
          ? error.message
          : 'We could not email your result. It is shown below in full.',
    }
  }
}

/**
 * POST /api/files/download
 *
 * The route runs the access check, then generates a short lived signed URL from
 * object storage and logs the download with user, file and timestamp. The raw
 * storage path is never sent to the browser.
 */
export async function requestDownloadUrl(
  fileName: string,
  context: { kind: 'deliverable' | 'invoice' | 'resource'; id?: string },
  /** A public copy of the file, served while the signed route is not yet live. */
  fallbackUrl?: string,
): Promise<{ url: string | null; error?: string; signed?: boolean }> {
  if (LIVE_API) {
    try {
      const result = await apiPost<{ url: string }>(ENDPOINTS.download, { fileName, ...context })
      return { url: result.url, signed: true }
    } catch (error) {
      const message =
        error instanceof ApiRequestError
          ? error.message
          : 'We could not prepare that download. Please try again.'
      return { url: null, error: message }
    }
  }

  if (fallbackUrl) return { url: fallbackUrl, signed: false }

  const expires = Math.floor(Date.now() / 1000) + 900
  const token = Math.random().toString(36).slice(2, 18)
  return {
    url: `https://files.cavalladata.com/d/${encodeURIComponent(fileName)}?expires=${expires}&signature=${token}`,
  }
}
